import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ShieldCheck, Settings2, Plus, Sparkles, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ToolBuilderDialog, AiToolGenerateDialog } from "@/components/tool-builder/tool-builder-dialog";

interface BotToolRow {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  category: string | null;
  requiresApproval: string;
  allowedRoles: string[];
  perUserPerHourLimit: number | null;
  perOrgPerHourLimit: number | null;
  enabled: boolean;
  integration: string | null;
}

// Turn an integration key ("zendesk", "builtin", "ms_graph") into a friendly label.
function integrationLabel(key: string): string {
  if (key === "builtin") return "Built-in";
  return key
    .split(/[_\s-]+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Canonical role names mirror the values used by the SCIM importer and the
// users.role column. Keep this list in sync with shared/schema.ts.
const DEFAULT_ROLES = [
  "user",
  "employee",
  "manager",
  "admin",
  "hr_admin",
  "it_admin",
  "bot_admin",
  "org_admin",
  "super_admin",
];

export default function ChatbotToolsPage() {
  const params = useParams();
  const chatbotId = params.id!;
  const { toast } = useToast();
  const [editing, setEditing] = useState<BotToolRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [builderToolId, setBuilderToolId] = useState<string | null>(null);
  const [integrationFilter, setIntegrationFilter] = useState<string>("all");

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "tools"] }),
      queryClient.invalidateQueries({ queryKey: ["/api/tools"] }),
    ]);

  const restoreTool = async (id: string) => {
    try {
      await apiRequest("POST", `/api/tools/${id}/restore`);
      await invalidate();
      toast({ description: "Tool restored." });
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message || "Restore failed." });
    }
  };

  const onDelete = async (t: BotToolRow) => {
    if (!window.confirm(`Archive tool "${t.displayName}"? This will turn it off for every bot. Its configuration and history are retained.`)) return;
    // Capture the archived tool ID so Undo can restore exactly this tool.
    const archivedToolId = t.id;
    try {
      await apiRequest("DELETE", `/api/tools/${t.id}`);
      toast({
        description: "Tool archived.",
        action: (
          <ToastAction altText={`Undo — restore tool "${t.displayName}"`} onClick={() => restoreTool(archivedToolId)} data-testid="button-undo-archive-tool">
            Undo
          </ToastAction>
        ),
      });
      await invalidate();
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message || "Archive failed." });
    }
  };

  const { data: tools, isLoading } = useQuery<BotToolRow[]>({
    queryKey: ["/api/chatbots", chatbotId, "tools"],
  });

  const countByIntegration = (tools ?? []).reduce<Record<string, number>>((acc, t) => {
    const k = t.integration || "builtin";
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  const integrations = Object.keys(countByIntegration).sort((a, b) =>
    integrationLabel(a).localeCompare(integrationLabel(b)),
  );

  // Reset a stale filter if the selected integration is no longer present
  // (e.g. its last tool was deleted, or the data otherwise changed).
  useEffect(() => {
    if (integrationFilter !== "all" && !integrations.includes(integrationFilter)) {
      setIntegrationFilter("all");
    }
  }, [integrationFilter, integrations]);

  const visibleTools = (tools ?? []).filter(
    (t) => integrationFilter === "all" || (t.integration || "builtin") === integrationFilter,
  );

  const onToggle = async (toolId: string, enabled: boolean) => {
    try {
      await apiRequest("POST", `/api/chatbots/${chatbotId}/tools/${toolId}`, { enabled });
      await queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "tools"] });
      toast({ description: enabled ? "Tool enabled for this bot." : "Tool disabled." });
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message || "Failed to update tool." });
    }
  };

  return (
    <div className="p-6 pb-28 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link href={`/chatbots/${chatbotId}`}>
          <Button variant="ghost" size="sm" data-testid="link-back-to-bot">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to bot
          </Button>
        </Link>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-[16rem]">
          <h1 className="text-2xl font-semibold tracking-tight">Tools & Actions</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose which actions this bot is allowed to perform on behalf of your staff.
            Tools marked <Badge variant="secondary" className="mx-1">Approval</Badge>
            require manager sign-off before they run. Click <Settings2 className="inline w-3 h-3" /> Edit policy
            to change who can use a tool, whether it needs approval, and how often it can run.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="button-ai-generate-tool">
            <Sparkles className="w-4 h-4 mr-1" /> AI Generate
          </Button>
          <Button onClick={() => setCreating(true)} data-testid="button-new-tool">
            <Plus className="w-4 h-4 mr-1" /> New Tool
          </Button>
        </div>
      </div>

      {!isLoading && tools && tools.length > 0 && integrations.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-sm text-muted-foreground">Integration</Label>
          <Select value={integrationFilter} onValueChange={setIntegrationFilter}>
            <SelectTrigger className="w-56" data-testid="select-integration-filter">
              <SelectValue placeholder="All integrations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="option-integration-all">
                All integrations ({tools.length})
              </SelectItem>
              {integrations.map((key) => (
                <SelectItem key={key} value={key} data-testid={`option-integration-${key}`}>
                  {integrationLabel(key)} ({countByIntegration[key]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground" data-testid="text-tool-count">
            {visibleTools.length} of {tools.length}
          </span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      )}

      {!isLoading && tools && tools.length === 0 && (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          No tools have been registered for your organization yet.
        </CardContent></Card>
      )}

      {!isLoading && tools && tools.length > 0 && visibleTools.length === 0 && (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="text-no-filtered-tools">
          No tools for this integration.
        </CardContent></Card>
      )}

      {!isLoading && visibleTools.map((t) => (
        <Card key={t.id} data-testid={`card-tool-${t.name}`}>
          {/* flex-wrap: at 320px the action buttons drop below the title block
              instead of crushing it into a one-word column. */}
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
            <div className="space-y-1 min-w-0 flex-1 basis-[14rem]">
              <CardTitle className="text-base flex flex-wrap items-center gap-2">
                <span className="break-words min-w-0">{t.displayName}</span>
                {t.requiresApproval === "true" && (
                  <Badge variant="secondary" className="gap-1">
                    <ShieldCheck className="w-3 h-3" /> Approval
                  </Badge>
                )}
                <Badge variant="outline" data-testid={`badge-integration-${t.name}`}>
                  {integrationLabel(t.integration || "builtin")}
                </Badge>
                {t.category && <Badge variant="outline">{t.category}</Badge>}
              </CardTitle>
              <CardDescription>{t.description || "No description"}</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(t)}
                data-testid={`button-edit-policy-${t.name}`}
              >
                <Settings2 className="w-4 h-4 mr-1" /> Policy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBuilderToolId(t.id)}
                data-testid={`button-edit-tool-${t.name}`}
              >
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(t)}
                aria-label={`Archive tool ${t.displayName || t.name}`}
                data-testid={`button-delete-tool-${t.name}`}
              >
                <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
              </Button>
              <Switch
                checked={t.enabled}
                onCheckedChange={(v) => onToggle(t.id, v)}
                aria-label={`${t.enabled ? "Disable" : "Enable"} tool ${t.name}`}
                data-testid={`switch-tool-${t.name}`}
              />
            </div>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1">
            {(t.allowedRoles?.length ?? 0) > 0 && (
              <div>Available to: {t.allowedRoles.join(", ")}</div>
            )}
            <div>
              Limits: {t.perUserPerHourLimit ?? "—"}/user/hr · {t.perOrgPerHourLimit ?? "—"}/org/hr
            </div>
          </CardContent>
        </Card>
      ))}

      <ToolBuilderDialog
        open={creating}
        onClose={() => setCreating(false)}
        chatbotId={chatbotId}
      />
      <ToolBuilderDialog
        open={!!builderToolId}
        toolId={builderToolId ?? undefined}
        onClose={() => setBuilderToolId(null)}
        chatbotId={chatbotId}
      />
      <AiToolGenerateDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        chatbotId={chatbotId}
      />

      {editing && (
        <PolicyDialog
          // Keying by tool.id forces a fresh component (and fresh local state)
          // every time a different tool's "Edit policy" is opened, preventing
          // stale values from leaking across tools and being saved back.
          key={editing.id}
          tool={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "tools"] });
            await queryClient.invalidateQueries({ queryKey: ["/api/tools"] });
          }}
        />
      )}
    </div>
  );
}

const NOTIFICATION_CHANNELS = [
  { value: "in_app",  label: "In-App" },
  { value: "email",   label: "Email" },
  { value: "slack",   label: "Slack" },
  { value: "teams",   label: "Microsoft Teams" },
  { value: "webhook", label: "Webhook" },
];

function PolicyDialog({
  tool, onClose, onSaved,
}: {
  tool: BotToolRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [roles, setRoles] = useState<string[]>(tool?.allowedRoles ?? []);
  const [requiresApproval, setRequiresApproval] = useState<boolean>(tool?.requiresApproval === "true");
  const [perUser, setPerUser] = useState<string>(tool?.perUserPerHourLimit?.toString() ?? "");
  const [perOrg, setPerOrg] = useState<string>(tool?.perOrgPerHourLimit?.toString() ?? "");
  // Approval settings (loaded from GET /api/tools/{id} when dialog opens).
  const [approverGroup, setApproverGroup] = useState<string>("");
  const [approvalTtlHours, setApprovalTtlHours] = useState<string>("48");
  const [autoApproveThreshold, setAutoApproveThreshold] = useState<string>("");
  const [notificationChannels, setNotificationChannels] = useState<string[]>(["in_app"]);
  const [loadingAction, setLoadingAction] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load per-action approval fields when the dialog opens.
  const loadActionFields = async (id: string) => {
    setLoadingAction(true);
    try {
      const res = await fetch(`/api/tools/${id}`, {
        headers: (() => {
          try { const t = localStorage.getItem("dotnetbot.jwt"); if (t) return { Authorization: `Bearer ${t}` } as HeadersInit; }
          catch { /* ignore */ }
          return undefined;
        })(),
      });
      if (!res.ok) return;
      const data = await res.json();
      // Approval policy lives on the tool (data.tool) not actions (works for registry + HTTP tools).
      const td = data.tool ?? data;
      if (td) {
        setApproverGroup(td.approverGroup ?? "");
        setApprovalTtlHours(td.approvalTtlHours?.toString() ?? "48");
        setAutoApproveThreshold(td.autoApproveThreshold != null ? td.autoApproveThreshold.toString() : "");
        try {
          const ch: string[] = td.notificationChannelsJson ? JSON.parse(td.notificationChannelsJson) : ["in_app"];
          setNotificationChannels(ch);
        } catch { setNotificationChannels(["in_app"]); }
      }
    } catch { /* best-effort */ } finally { setLoadingAction(false); }
  };

  const toggleRole = (r: string) => {
    setRoles((prev) => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const toggleChannel = (ch: string) => {
    setNotificationChannels((prev) => prev.includes(ch) ? prev.filter(x => x !== ch) : [...prev, ch]);
  };

  const save = async () => {
    if (!tool) return;
    setSaving(true);
    try {
      const body: any = {
        allowedRoles: roles,
        requiresApproval,
        perUserPerHourLimit: perUser === "" ? null : Number(perUser),
        perOrgPerHourLimit: perOrg === "" ? null : Number(perOrg),
        approverGroup: approverGroup.trim() || null,
        approvalTtlHours: approvalTtlHours === "" ? 48 : Math.max(1, Number(approvalTtlHours)),
        autoApproveThreshold: autoApproveThreshold === "" ? null : Number(autoApproveThreshold),
        notificationChannelsJson: JSON.stringify(notificationChannels),
      };
      await apiRequest("PATCH", `/api/tools/${tool.id}`, body);
      toast({ description: "Tool policy updated." });
      onSaved();
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message || "Failed to update policy." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={!!tool}
      onOpenChange={(o) => {
        if (!o) onClose();
        else if (tool) {
          setRoles(tool.allowedRoles ?? []);
          setRequiresApproval(tool.requiresApproval === "true");
          setPerUser(tool.perUserPerHourLimit?.toString() ?? "");
          setPerOrg(tool.perOrgPerHourLimit?.toString() ?? "");
          loadActionFields(tool.id);
        }
      }}
    >
      <DialogContent data-testid="dialog-policy" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tool policy — {tool?.displayName}</DialogTitle>
          <DialogDescription>
            Control who can trigger this tool, whether it needs human approval, and how often it can run.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {/* Allowed Roles */}
          <div>
            <Label className="text-sm">Allowed roles</Label>
            <p className="text-xs text-muted-foreground mb-2">
              An empty list means everyone in the org can use it.
            </p>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_ROLES.map((r) => {
                const on = roles.includes(r);
                return (
                  <button key={r} type="button" onClick={() => toggleRole(r)}
                    data-testid={`role-toggle-${r}`}
                    className={`px-2 py-1 rounded-md text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground"}`}>
                    {r}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Requires Approval toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Requires approval</Label>
              <p className="text-xs text-muted-foreground">When on, every call is queued for a human approver.</p>
            </div>
            <Switch checked={requiresApproval} onCheckedChange={setRequiresApproval}
              data-testid="switch-requires-approval" />
          </div>

          {/* Approval Settings — shown when requiresApproval is ON */}
          {requiresApproval && (
            <div className="space-y-4 pl-3 border-l-2 border-primary/20 ml-1">
              {loadingAction && (
                <p className="text-xs text-muted-foreground">Loading approval settings…</p>
              )}

              {/* Approver Group */}
              <div>
                <Label htmlFor="approver-group" className="text-sm">Approver Group</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Role name whose members can approve/reject (e.g. <code>manager</code>, <code>OrgAdmin</code>).
                  Leave blank to use manager chain or org admin fallback.
                </p>
                <Input
                  id="approver-group"
                  placeholder="e.g. manager"
                  value={approverGroup}
                  onChange={(e) => setApproverGroup(e.target.value)}
                  data-testid="input-approver-group"
                />
              </div>

              {/* TTL */}
              <div>
                <Label htmlFor="approval-ttl" className="text-sm">Approval TTL (hours)</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Pending requests are auto-rejected after this many hours (default: 48).
                </p>
                <Input
                  id="approval-ttl"
                  type="number" min="1" placeholder="48"
                  value={approvalTtlHours}
                  onChange={(e) => setApprovalTtlHours(e.target.value)}
                  data-testid="input-approval-ttl"
                />
              </div>

              {/* Auto-Approve Threshold */}
              <div>
                <Label htmlFor="auto-approve" className="text-sm">Auto-Approve Threshold</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  If the primary numeric argument in the request is ≤ this value, the action auto-approves
                  without entering the queue. Leave blank to always require human review.
                  <strong> Comparison rule: ≤ (less-than-or-equal).</strong>
                </p>
                <Input
                  id="auto-approve"
                  type="number" min="1" placeholder="No auto-approve"
                  value={autoApproveThreshold}
                  onChange={(e) => setAutoApproveThreshold(e.target.value)}
                  data-testid="input-auto-approve-threshold"
                />
              </div>

              {/* Notification Channels */}
              <div>
                <Label className="text-sm">Notification Channels</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Channels used to notify the approver when a request is created.
                </p>
                <div className="flex flex-wrap gap-2">
                  {NOTIFICATION_CHANNELS.map(({ value, label }) => {
                    const on = notificationChannels.includes(value);
                    return (
                      <button key={value} type="button" onClick={() => toggleChannel(value)}
                        data-testid={`channel-toggle-${value}`}
                        className={`px-2 py-1 rounded-md text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-transparent text-muted-foreground"}`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Rate Limits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="per-user" className="text-sm">Per-user per hour</Label>
              <Input id="per-user" type="number" min="0" placeholder="No limit"
                value={perUser} onChange={(e) => setPerUser(e.target.value)}
                data-testid="input-per-user-limit" />
            </div>
            <div>
              <Label htmlFor="per-org" className="text-sm">Per-org per hour</Label>
              <Input id="per-org" type="number" min="0" placeholder="No limit"
                value={perOrg} onChange={(e) => setPerOrg(e.target.value)}
                data-testid="input-per-org-limit" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="button-cancel-policy">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="button-save-policy">
            {saving ? "Saving…" : "Save policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
